"use client";

import {
  Collector,
  Container,
  makeBusinessIdSetter,
  makeEventWithPageViewId,
  type OmitPageViewId,
  trackerPlugin,
} from "@grupa-pracuj/analytics-plugin-gp-tracker";

import { schema as pageView } from "@grupa-pracuj/tracker-schematics/events/pl.pracuj.tracker/pageview/3-0-0";
import {
  type Properties as CookieModal,
  schema as cookieAgreementsModal,
} from "@grupa-pracuj/tracker-schematics/events/pl.pracuj.www/cookieagreementsmodal/1-0-0";
import {
  type Properties as WebVitals,
  schema as webVitals,
} from "@grupa-pracuj/tracker-schematics/events/pl.pracuj.www/webvitals/1-0-1";
import Analytics, { type AnalyticsInstance } from "analytics";
import {
  createContext,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from "react";
import type { Metric } from "web-vitals";
import type { User } from "@/types";
import { collectWebVitals } from "./WebVitals";

type GenericEventMethod<T> = (data: OmitPageViewId<T>) => Promise<unknown>;

type BaseEventMethod = () => Promise<unknown>;

type TrackerProperties = {
  analyticsInstance: AnalyticsInstance;
  sendBaseAnalytics: BaseEventMethod;
  sendCookieAgreementsModal: GenericEventMethod<CookieModal>;
};

export const TrackerContext = createContext<TrackerProperties | null>(null);

export const TrackerProvider = ({
  children,
  environment,
  appName,
  appVersion,
  user,
}: {
  children: ReactNode;
  environment: string;
  appName: string;
  appVersion: string;
  user?: User | null;
}) => {
  const isProduction = environment === "production";

  const [analyticsInstance] = useState(() =>
    Analytics({
      app: appName,
      debug: !isProduction,
      plugins: [
        trackerPlugin({
          appName,
          appVersion: `${appVersion}`,
          collector: isProduction ? Collector.prod : Collector.beta,
          container: Container.pracuj,
          events: {
            cookieAgreementsModal,
            pageView,
            webVitals,
          },
        }),
      ],
    }),
  );

  const sendPageView = useCallback(async () => {
    const userId = user?.userId ?? "";
    const businessId = String(userId);
    const previousUrl = analyticsInstance.getState(
      "page.last.properties.url",
    ) as string | undefined;
    const referrer = previousUrl ?? document.referrer;

    await makeBusinessIdSetter(analyticsInstance)(businessId);
    await analyticsInstance.page({
      lan: "pl",
      url: globalThis.location.href,
      referrer,
    });
  }, [analyticsInstance, user?.userId]);

  const webVitalsInstance = useCallback(
    makeEventWithPageViewId<WebVitals>(analyticsInstance, "webVitals"),
    [],
  );

  const sendWebVitals = useCallback(
    async ({ name, delta, id, value: metricValue }: Metric) => {
      await webVitalsInstance({ id, name, value: metricValue, delta });
    },
    [webVitalsInstance],
  );

  const sendBaseAnalytics = useCallback(async () => {
    await sendPageView();
    collectWebVitals((metric) => {
      sendWebVitals(metric).catch(() => {});
    });
  }, [sendPageView, sendWebVitals]);

  const cookieAgreementsModalInstance = useCallback(
    makeEventWithPageViewId<CookieModal>(
      analyticsInstance,
      "cookieAgreementsModal",
    ),
    [],
  );

  const sendCookieAgreementsModal = useCallback(
    async (data: OmitPageViewId<CookieModal>) => {
      await cookieAgreementsModalInstance(data);
    },
    [cookieAgreementsModalInstance],
  );

  const value = useMemo(
    () => ({
      analyticsInstance,
      sendCookieAgreementsModal,
      sendBaseAnalytics,
    }),
    [analyticsInstance, sendCookieAgreementsModal, sendBaseAnalytics],
  );

  return (
    <TrackerContext.Provider value={value}>{children}</TrackerContext.Provider>
  );
};
